import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, App, Button, Form, InputNumber, Modal, Segmented, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { LockOutlined } from '@ant-design/icons';

import { useAccess } from '../../auth/AccessContext.jsx';
import ActionIcon from '../../components/ActionIcon.jsx';
import { useLiveTopic } from '../../live/LiveContext.js';

const { Text } = Typography;

/** Each severity's tag colour - display only. */
const SEVERITY_COLOR = { CRITICAL: 'red', WARNING: 'orange', ACTION: 'blue', INFO: 'default' };

const NotificationRecipients = ({ notification }) => {
  if (!notification.subscribable || !notification.roles.length) {
    return <Text type="secondary">{notification.subscribable ? 'No role assigned' : 'Affected user'}</Text>;
  }

  const roleRows = [];
  for (let index = 0; index < notification.roles.length; index += 2) {
    roleRows.push(notification.roles.slice(index, index + 2));
  }

  return (
    <div className="notification-role-rows">
      {roleRows.map((roles) => (
        <div className="notification-role-row" key={roles.join('-')}>
          {roles.map((role) => <Tag key={role}>{role}</Tag>)}
        </div>
      ))}
    </div>
  );
};

/**
 * User Management > Notifications (docs/notifications/implementation-guide.md 8.2):
 * every notification the Core sends, and the three settings an administrator
 * may change for each - NOTIFICATION_CONFIG_SAVE (migration 0025):
 *
 *   Enabled     whether it is sent at all
 *   Email       whether it is also emailed (it always appears in the Console)
 *   Cooldown    at most one email of it per person per this many minutes
 *
 * Which ROLES receive a notification is set on the role (the Roles tab's
 * dialog), beside its permissions; this list shows them. A notification
 * reaches a person only when a role of theirs subscribes AND they hold one of
 * its required permissions.
 *
 * A security notice (authenticator reset, password changed, step-up locked)
 * is always sent, always by email, and cannot be muted: its switches are
 * locked here and the server refuses them anyway.
 */
const NotificationsPanel = () => {
  const { api, canAct } = useAccess();
  const { message } = App.useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const [form] = Form.useForm();
  // The server refuses anyone without core.access.admin; this only decides whether Edit shows.
  const canEdit = canAct('userManagement', 'editNotifications');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { notifications } = await api.access.notifications();
      setRows(notifications);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api]);
  // Live screens (docs/live-sync/implementation-plan.md 4.6): re-read, exactly as Refresh does, when
  // another administrator changed a notification's settings.
  useLiveTopic(['access'], load);

  useEffect(() => {
    load();
  }, [load]);

  const open = (row) => {
    setDialogError(null);
    setEditing(row);
    form.setFieldsValue({
      enabled: row.enabled ? 'YES' : 'NO',
      forceEmail: row.forceEmail ? 'YES' : 'NO',
      emailCooldownMinutes: row.emailCooldownMinutes,
    });
  };

  const save = async (values) => {
    setBusy(true);
    setDialogError(null);
    try {
      await api.access.saveNotificationConfig(editing.code, {
        enabled: values.enabled === 'YES',
        forceEmail: values.forceEmail === 'YES',
        emailCooldownMinutes: Number(values.emailCooldownMinutes) || 0,
      });
      message.success(editing.label + ' saved. It applies to the next notification.');
      setEditing(null);
      await load();
    } catch (e) {
      setDialogError(e.message);
    } finally {
      setBusy(false);
    }
  };

  // Grouped by Console section, in the server's order within each.
  const data = useMemo(() => [...rows].sort((a, b) => (a.group === b.group ? 0 : a.group < b.group ? -1 : 1)), [rows]);

  const columns = [
    { title: 'Section', dataIndex: 'group', key: 'group', width: 150 },
    {
      title: 'Notification',
      key: 'label',
      render: (_, r) => (
        <>
          <span className="notification-title-line">
            <Text>{r.label}</Text>
            <Tooltip title={r.help}>
              <span className="notification-info-icon"><ActionIcon name="info" /></span>
            </Tooltip>
            {r.emailLocked && <Tooltip title="A security notice: always sent, always emailed, cannot be muted."><LockOutlined /></Tooltip>}
          </span>
          <div className="claim-code">{r.code}</div>
          <Tag color={SEVERITY_COLOR[r.severity]}>{r.severity}</Tag>
        </>
      ),
    },
    {
      title: 'Who receives it',
      key: 'who',
      width: 260,
      render: (_, r) => <NotificationRecipients notification={r} />,
    },
    { title: 'Enabled', key: 'enabled', width: 90, render: (_, r) => (r.enabled ? <Tag color="green">on</Tag> : <Tag>off</Tag>) },
    { title: 'Email', key: 'email', width: 90, render: (_, r) => (r.forceEmail ? <Tag color="blue">EMAIL</Tag> : <Text type="secondary">In-App</Text>) },
    { title: 'Cooldown', key: 'cooldown', width: 100, render: (_, r) => (r.emailCooldownMinutes ? r.emailCooldownMinutes + ' min' : '—') },
    {
      title: '',
      key: 'actions',
      width: 148,
      render: (_, r) => (canEdit && !r.emailLocked
        ? <Button className="role-action-button permission-edit-button" icon={<ActionIcon name="edit" />} onClick={() => open(r)}>Edit</Button>
        : null),
    },
  ];

  return (
    <section className="panel permission-panel">
      <div className="permission-toolbar notifications-toolbar">
        <div className="permission-panel-heading">
          <h3 className="panel-title">Notifications</h3>
          <div className="permission-info-banner notifications-info-banner">
            <ActionIcon name="info" />
            <span>Roles determine which notifications users receive. Each notification can be enabled or disabled and configured for email delivery.</span>
          </div>
        </div>
        <Space className="permission-toolbar-actions">
          <Button className="refresh-button" icon={<ActionIcon name="refresh" />} onClick={load} loading={loading}>Refresh</Button>
        </Space>
      </div>
      {error && <Alert type="error" showIcon message={error} className="auth-alert" />}
      <Table className="permission-table notifications-admin-table" rowKey="code" size="small" loading={loading} columns={columns} dataSource={data}
        pagination={false} tableLayout="fixed" scroll={{ x: 1060 }} />

      <Modal open={Boolean(editing)} title={editing ? 'Edit: ' + editing.label : ''} onCancel={() => setEditing(null)}
        onOk={() => form.submit()} okText="Save" okButtonProps={{ loading: busy }} width={680}
        rootClassName="lfoc-styles-wrapper notification-edit-modal" destroyOnHidden>
        {dialogError && <Alert type="error" showIcon message={dialogError} className="auth-alert" />}
        <Form className="notification-edit-form" form={form} layout="vertical" requiredMark={false} onFinish={save}>
          <Form.Item name="enabled" label="Sent" extra="Nobody receives it, in the Console or by email.">
            <Segmented
              className="notification-receive-toggle notification-edit-toggle"
              options={[{ label: 'Yes', value: 'YES' }, { label: 'No', value: 'NO' }]}
            />
          </Form.Item>
          <Form.Item name="forceEmail" label="Also by Email" extra="In the Console only">
            <Segmented
              className="notification-receive-toggle notification-edit-toggle"
              options={[{ label: 'Yes', value: 'YES' }, { label: 'No', value: 'NO' }]}
            />
          </Form.Item>
          <Form.Item name="emailCooldownMinutes" label="Email cooldown (minutes)"
            extra="At most one email of this per person in this many minutes. 0: no limit. Every in-app item still lands.">
            <InputNumber min={0} max={1440} precision={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </section>
  );
};

export default NotificationsPanel;
